import { useState, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, RefreshCw, Pencil, Trash2, Search, X } from 'lucide-react'
import { useAccount } from '@/contexts/AccountContext'
import { CollectionEntryModal } from './CollectionEntryModal'
import type { CollectionConfig } from '../config/collectionRegistry'

interface CollectionBrowserProps {
  config: CollectionConfig
  listFn: (accId: number) => Promise<Record<string, unknown>[]>
  createFn: (accId: number, data: Record<string, unknown>) => Promise<unknown>
  updateFn: (id: string, data: Record<string, unknown>) => Promise<unknown>
  deleteFn: (id: string) => Promise<unknown>
  idKey?: string
}

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-500',
}

const TOKEN_TYPE_BADGE: Record<string, string> = {
  system: 'bg-blue-100 text-blue-700',
  custom: 'bg-purple-100 text-purple-700',
  user: 'bg-orange-100 text-orange-700',
}

const SOURCE_TYPE_BADGE: Record<string, string> = {
  customer: 'bg-teal-100 text-teal-700',
  event: 'bg-amber-100 text-amber-700',
  user: 'bg-indigo-100 text-indigo-700',
  segment: 'bg-pink-100 text-pink-700',
}

const DATA_TYPE_BADGE: Record<string, string> = {
  string: 'bg-sky-100 text-sky-700',
  number: 'bg-emerald-100 text-emerald-700',
  boolean: 'bg-violet-100 text-violet-700',
  date: 'bg-rose-100 text-rose-700',
  array: 'bg-lime-100 text-lime-700',
}

export function CollectionBrowser({
  config,
  listFn,
  createFn,
  updateFn,
  deleteFn,
  idKey = 'id',
}: CollectionBrowserProps) {
  const { selectedAccountId } = useAccount()
  const queryClient = useQueryClient()

  const [modalState, setModalState] = useState<
    { mode: 'create' } | { mode: 'edit'; record: Record<string, unknown> } | null
  >(null)
  const [deleteTarget, setDeleteTarget] = useState<Record<string, unknown> | null>(null)
  const [mutationError, setMutationError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const accId = selectedAccountId && selectedAccountId !== 'ALL'
    ? parseInt(selectedAccountId, 10)
    : null

  const { data = [], isLoading, isError, refetch, isFetching } = useQuery<Record<string, unknown>[]>({
    queryKey: [config.queryKey, accId],
    queryFn: () => {
      if (!accId) return Promise.resolve([])
      return listFn(accId)
    },
    enabled: !!accId,
    staleTime: 30_000,
  })

  const filtered = useMemo(() => {
    if (!search.trim()) return data
    const q = search.toLowerCase()
    const searchKey = config.searchKey
    return data.filter((r) => {
      if (searchKey) {
        const primary = String(r[searchKey] ?? '').toLowerCase()
        if (primary.includes(q)) return true
      }
      // Also search displayName if it exists
      const displayName = String(r['displayName'] ?? '').toLowerCase()
      return displayName.includes(q)
    })
  }, [data, search, config.searchKey])

  const saveMutation = useMutation({
    mutationFn: async (payload: { values: Record<string, unknown>; id?: string }) => {
      if (!accId) throw new Error('No account selected')
      if (payload.id) return updateFn(payload.id, payload.values)
      return createFn(accId, payload.values)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [config.queryKey, accId] })
      setModalState(null)
      setMutationError(null)
    },
    onError: (err: Error) => setMutationError(err.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFn(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [config.queryKey, accId] })
      setDeleteTarget(null)
    },
  })

  const tableFields = config.fields
    .filter((f) => f.tableVisible && config.tableColumns.includes(f.key))
    .sort((a, b) => config.tableColumns.indexOf(a.key) - config.tableColumns.indexOf(b.key))

  const getCellContent = (field: (typeof tableFields)[number], record: Record<string, unknown>) => {
    const val = record[field.key]
    const str = val != null && val !== '' ? String(val) : '—'

    if (field.key === 'status') {
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[str] ?? 'bg-gray-100 text-gray-600'}`}>
          {str}
        </span>
      )
    }
    if (field.key === 'type') {
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TOKEN_TYPE_BADGE[str] ?? 'bg-gray-100 text-gray-600'}`}>
          {str}
        </span>
      )
    }
    if (field.key === 'sourceType') {
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_TYPE_BADGE[str] ?? 'bg-gray-100 text-gray-600'}`}>
          {str}
        </span>
      )
    }
    if (field.key === 'dataType') {
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${DATA_TYPE_BADGE[str] ?? 'bg-gray-100 text-gray-600'}`}>
          {str}
        </span>
      )
    }
    if (field.tableBadge) {
      return (
        <code className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-800 text-xs font-mono">
          {`{{${str}}}`}
        </code>
      )
    }
    return <span className="text-gray-700">{str}</span>
  }

  const openCreate = () => { setMutationError(null); setModalState({ mode: 'create' }) }
  const openEdit = (record: Record<string, unknown>) => { setMutationError(null); setModalState({ mode: 'edit', record }) }

  const handleSubmit = (values: Record<string, unknown>) => {
    const id = modalState?.mode === 'edit' ? String(modalState.record[idKey]) : undefined
    saveMutation.mutate({ values, id })
  }

  if (!accId) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-600">No account selected</p>
        <p className="text-xs text-gray-400 mt-1">
          Choose an account from the top bar to view and manage tokens.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tokens…"
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Count */}
        {!isLoading && !isError && (
          <span className="text-sm text-gray-400">
            {search
              ? `${filtered.length} of ${data.length}`
              : `${data.length} token${data.length !== 1 ? 's' : ''}`}
          </span>
        )}

        <div className="flex-1" />

        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Add Token
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-16 text-center">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-400">Loading tokens…</p>
          </div>
        ) : isError ? (
          <div className="p-16 text-center">
            <p className="text-sm text-red-500 font-medium">Failed to load data</p>
            <button onClick={() => refetch()} className="mt-2 text-sm text-primary hover:underline">
              Try again
            </button>
          </div>
        ) : data.length === 0 ? (
          <div className="p-16 text-center">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Plus className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-600">No tokens yet</p>
            <p className="text-xs text-gray-400 mt-1 mb-4">Get started by creating the first personalized token.</p>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Add Token
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-sm text-gray-500">No tokens match "<strong>{search}</strong>"</p>
            <button onClick={() => setSearch('')} className="mt-2 text-sm text-primary hover:underline">
              Clear search
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {tableFields.map((f) => (
                    <th
                      key={f.key}
                      className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                    >
                      {f.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((record, idx) => (
                  <tr
                    key={String(record[idKey] ?? idx)}
                    className="hover:bg-gray-50 group"
                  >
                    {tableFields.map((f) => (
                      <td key={f.key} className="px-4 py-3 whitespace-nowrap">
                        {getCellContent(f, record)}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openEdit(record)}
                          className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(record)}
                          className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {modalState && (
        <CollectionEntryModal
          config={config}
          existing={modalState.mode === 'edit' ? modalState.record : undefined}
          isSaving={saveMutation.isPending}
          error={mutationError}
          onClose={() => { setModalState(null); setMutationError(null) }}
          onSubmit={handleSubmit}
        />
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-6 space-y-3">
              <h3 className="text-base font-semibold text-gray-900">Delete Token</h3>
              <p className="text-sm text-gray-600">
                Delete{' '}
                <code className="px-1.5 py-0.5 rounded bg-gray-100 text-xs font-mono">
                  {`{{${String(deleteTarget['tokenName'] ?? deleteTarget[idKey])}}}`}
                </code>
                ? This cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(String(deleteTarget[idKey]))}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
